import React from 'react';

import { DisclosurePanel } from '../components/AppShellPrimitives';
import { findingOccurrenceLabel } from '../appHelpers';
import { EvidencePostureChip } from '../evidencePosture';

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function findingStatusLabel(status) {
  switch (status) {
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    case 'open':
    default:
      return 'Open';
  }
}

function prevalenceLabel(prevalence) {
  switch (prevalence) {
    case 'isolated':
      return 'Isolated';
    case 'recurring':
      return 'Recurring';
    case 'widespread':
      return 'Widespread';
    default:
      return 'Unknown';
  }
}

function componentLabel(component) {
  if (!component) {
    return null;
  }
  return component.display_name || component.name;
}

function ownerLabel(owner) {
  if (!owner) {
    return 'Unassigned';
  }
  return owner.full_name || owner.email || owner.id;
}

function workflowContextLabel(workflowName) {
  if (!workflowName) {
    return null;
  }
  return workflowName === 'Onboarded Workflow' ? 'Default workflow' : workflowName;
}

function impactRangeLabel(impactEstimate) {
  if (!impactEstimate) {
    return null;
  }
  const lower = impactEstimate.lower_bound;
  const upper = impactEstimate.upper_bound ?? lower;
  if (lower === undefined && upper === undefined) {
    return null;
  }
  if (upper === lower) {
    return `${upper}`;
  }
  return `${lower} to ${upper}`;
}

function humanizeImpactUnit(unit) {
  if (!unit) {
    return null;
  }
  return String(unit).replaceAll('_', ' ');
}

function findingSystemImpactSummary(finding) {
  const impactEstimate = finding?.impact_estimate;
  const range = impactRangeLabel(impactEstimate);
  if (!impactEstimate || !range) {
    return null;
  }

  switch (impactEstimate.unit) {
    case 'unguarded_actions_per_100_runs':
      return `About ${range} risky actions may proceed without validation in every 100 runs.`;
    case 'at_risk_runs_per_100':
      return `About ${range} runs in every 100 may be at risk of failure or instability.`;
    case 'at_risk_io_steps_per_100':
      return `About ${range} retrieval or external steps in every 100 may be at risk.`;
    case 'seconds_per_run':
      return `Affected runs may take about ${range} seconds longer to complete.`;
    case 'tokens_per_run':
      return `The workflow may spend about ${range} extra tokens per run.`;
    case 'extra_steps_per_run':
      return `The workflow may perform about ${range} extra steps each run.`;
    case 'extra_loop_steps_per_run':
      return `The workflow may spend about ${range} extra loop steps before it can finish.`;
    case 'human_handoffs_per_100_runs':
      return `About ${range} runs in every 100 may still require human handoff.`;
    case 'premature_handoffs_per_100_runs':
      return `About ${range} runs in every 100 may hand off to a person earlier than necessary.`;
    case 'invalid_sequences_per_100_runs':
      return `About ${range} runs in every 100 may enter an invalid or risky sequence.`;
    case 'rescued_runs_per_100':
      return `About ${range} runs in every 100 depend on fallback recovery to complete.`;
    case 'rewrite_dependent_runs_per_100':
      return `About ${range} runs in every 100 depend on hidden query rewrites to succeed.`;
    case 'irrelevant_docs_per_100_retrieved':
      return `About ${range} of every 100 retrieved documents may be low-value or irrelevant.`;
    case 'screening_calls_per_kept_doc':
      return `Keeping one useful document may require about ${range} screening passes.`;
    case 'uncited_answers_per_100_runs':
      return `About ${range} answer outputs in every 100 may surface without citations even when evidence is attached.`;
    case 'weakly_grounded_answers_per_100_runs':
      return `About ${range} answer outputs in every 100 may be weakly supported by evidence.`;
    case 'misclassified_successes_per_100_runs':
      return `About ${range} runs in every 100 may have a completed technical status despite conflicting terminal evidence.`;
    case 'reanalysed_source_items_per_100':
      return `About ${range} source items in every 100 may be re-analysed instead of carrying context forward.`;
    default: {
      const unitLabel = humanizeImpactUnit(impactEstimate.unit);
      return unitLabel
        ? `Estimated system impact is about ${range} ${unitLabel}.`
        : `Estimated system impact is about ${range}.`;
    }
  }
}

function severityClassName(severity) {
  switch ((severity || '').toLowerCase()) {
    case 'critical':
      return 'finding-card-critical';
    case 'high':
      return 'finding-card-high';
    case 'medium':
      return 'finding-card-medium';
    case 'low':
      return 'finding-card-low';
    default:
      return 'finding-card-neutral';
  }
}

function preparednessLabel(status) {
  switch (status) {
    case 'needs_labels':
      return 'Needs labels';
    case 'ready':
      return 'Pack ready';
    case 'validated':
      return 'Validated';
    case 'none':
    default:
      return 'Validate next';
  }
}

function targetedValidationTone(summary) {
  switch (summary?.preparedness_status) {
    case 'validated':
      return 'chip-success';
    case 'ready':
      return 'chip-emphasis';
    case 'needs_labels':
      return 'chip-alert';
    default:
      return 'chip-emphasis';
  }
}

function targetedValidationAction(summary, systemId, findingId) {
  if (summary?.active_pack_id) {
    return {
      href: `#/systems/${systemId}/eval-packs/${summary.active_pack_id}`,
      label: summary.preparedness_status === 'needs_labels' ? 'Open pack' : 'Review pack',
    };
  }
  return {
    href: `#/systems/${systemId}/findings/${findingId}`,
    label: 'Open finding',
  };
}

function coverageFlowLabel(status) {
  switch (status) {
    case 'failing_eval':
      return 'Failing eval';
    case 'passing_eval':
      return 'Passing eval';
    case 'covered_by_tests':
      return 'Tests created';
    case 'needs_labels':
      return 'Needs labels';
    case 'reviewed_in_eval_tool':
      return 'Reviewed in ASE';
    case 'dismissed_in_eval_tool':
      return 'Dismissed in ASE';
    case 'stale':
      return 'Stale';
    case 'sent':
      return 'Sent';
    case 'accepted_not_sent':
      return 'Accepted, not sent';
    case 'needs_more_evidence':
      return 'Needs evidence';
    case 'not_reviewed':
      return 'Not reviewed';
    default:
      return 'No flow yet';
  }
}

function coverageFlowTone(status) {
  switch (status) {
    case 'failing_eval':
    case 'stale':
    case 'needs_labels':
      return 'chip-alert';
    case 'passing_eval':
    case 'covered_by_tests':
      return 'chip-success';
    case 'sent':
    case 'reviewed_in_eval_tool':
    case 'accepted_not_sent':
      return 'chip-emphasis';
    default:
      return '';
  }
}

function coverageFlowHref(systemId, coverageRow) {
  const status = coverageRow?.evaluation_coverage?.status;
  return status
    ? `#/systems/${systemId}/runtime-coverage?coverage_status=${status}`
    : `#/systems/${systemId}/runtime-coverage`;
}

export default function FindingsListView({
  selectedSystem,
  findings,
  route,
  findingWorkflows,
  findingComponents,
  activeFilterChips,
  updateFindingsFilter,
  banner,
}) {
  const eligibleForValidationCount = findings.filter((finding) => finding.targeted_validation_summary?.eligibility_status === 'eligible').length;
  const activePackCount = findings.filter((finding) => Boolean(finding.targeted_validation_summary?.active_pack_id)).length;

  return (
    <section className="content-grid single-column">
      <div className="panel detail-panel">
        {banner}
        <div className="panel-header">
          <div>
            <p className="detail-kicker">Priority review</p>
            <h2>Findings</h2>
            <p className="panel-copy">
              The most impactful reliability issues detected across recent workflows.
            </p>
          </div>
          {activeFilterChips.length ? (
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings`}>
              Clear filters
            </a>
          ) : null}
        </div>

        <div className="findings-summary-grid">
          <div className="detail-card summary-card">
            <span className="summary-card-label">Active findings</span>
            <strong>{findings.length}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Validate next</span>
            <strong>{eligibleForValidationCount}</strong>
          </div>
          <div className="detail-card summary-card">
            <span className="summary-card-label">Active packs</span>
            <strong>{activePackCount}</strong>
          </div>
        </div>

        <DisclosurePanel title="Filters" className="filters-disclosure" summaryMeta={activeFilterChips.length ? `${activeFilterChips.length} active` : 'Optional'}>
          <div className="filters-grid">
            <label className="form-field">
              <span>Severity</span>
              <select className="text-input" value={route.severity || ''} onChange={(event) => updateFindingsFilter('severity', event.target.value)}>
                <option value="">Any severity</option>
                {['critical', 'high', 'medium', 'low'].map((severity) => (
                  <option value={severity} key={severity}>
                    {severity}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Status</span>
              <select className="text-input" value={route.status || ''} onChange={(event) => updateFindingsFilter('status', event.target.value)}>
                <option value="">Active issues</option>
                {['open', 'in_progress', 'resolved'].map((statusOption) => (
                  <option value={statusOption} key={statusOption}>
                    {findingStatusLabel(statusOption)}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Prevalence</span>
              <select className="text-input" value={route.prevalence || ''} onChange={(event) => updateFindingsFilter('prevalence', event.target.value)}>
                <option value="">Any prevalence</option>
                {['isolated', 'recurring', 'widespread'].map((prevalence) => (
                  <option value={prevalence} key={prevalence}>
                    {prevalenceLabel(prevalence)}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Workflow</span>
              <select className="text-input" value={route.workflowId || ''} onChange={(event) => updateFindingsFilter('workflowId', event.target.value)}>
                <option value="">Any workflow</option>
                {findingWorkflows.map((workflow) => (
                  <option value={workflow.id} key={workflow.id}>
                    {workflow?.name || ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>Component</span>
              <select className="text-input" value={route.componentId || ''} onChange={(event) => updateFindingsFilter('componentId', event.target.value)}>
                <option value="">Any component</option>
                {findingComponents.map((component) => (
                  <option value={component.id} key={component.id}>
                    {componentLabel(component)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </DisclosurePanel>

        {activeFilterChips.length ? (
          <div className="chip-row findings-filter-row">
            {activeFilterChips.map((chip) => (
              <span className="chip chip-emphasis" key={chip}>
                {chip}
              </span>
            ))}
          </div>
        ) : null}

        <div className="stack findings-triage-stack">
          {findings.map((finding, index) => {
            const targetedValidationSummary = finding.targeted_validation_summary;
            const targetedAction = targetedValidationAction(targetedValidationSummary, selectedSystem.id, finding.id);
            const systemImpactSummary = findingSystemImpactSummary(finding);
            const workflowLabel = workflowContextLabel(finding.workflow_name);
            const findingReference = `F-${String(finding.priority_rank || index + 1).padStart(2, '0')}`;
            const coverageRow = finding.runtime_risk_coverage_row;
            const evaluationCoverage = coverageRow?.evaluation_coverage;
            const coverageStatus = evaluationCoverage?.status || 'not_reviewed';
            const coverageMeta = coverageRow?.reviewed_scenario_label || evaluationCoverage?.summary || 'No accepted scenario review yet.';
            const validationLabel = targetedValidationSummary
              ? preparednessLabel(targetedValidationSummary.preparedness_status)
              : 'Validate next';

            return (
              <article className={`finding-card finding-triage-card ${severityClassName(finding.severity)}`} key={finding.id}>
                <div className="finding-triage-row">
                  <div className="finding-triage-main">
                    <div className="finding-card-meta">
                      <span className="finding-reference-badge" aria-label={`Finding reference ${findingReference}`}>{findingReference}</span>
                      <span className="finding-category-badge">{finding.primary_impact_category || 'analysis'}</span>
                      <span className="finding-severity-label">{finding.severity}</span>
                      <span className="finding-status-label">{findingStatusLabel(finding.status)}</span>
                    </div>

                    <div className="finding-title-group">
                      <span className="compare-finding-icon" aria-hidden="true" />
                      <div>
                        <h3>
                          <a className="finding-link" href={`#/systems/${selectedSystem.id}/findings/${finding.id}`}>
                            {finding.title}
                          </a>
                        </h3>
                        <p>{finding.customer_language_summary || finding.summary}</p>
                      </div>
                    </div>
                  </div>

                  <div className="finding-triage-signals" aria-label={`${findingReference} triage signals`}>
                    <div className="finding-score-pill">
                      <span className="summary-card-label">Priority</span>
                      <strong>{finding.priority_score}</strong>
                    </div>
                    {workflowLabel ? (
                      <span className="finding-workflow-context">
                        <span>Workflow</span>
                        <strong>{workflowLabel}</strong>
                      </span>
                    ) : null}
                    <span className={`chip ${targetedValidationTone(targetedValidationSummary)}`}>
                      {validationLabel}
                    </span>
                  </div>
                </div>

                <div className="finding-triage-proof-row">
                  {systemImpactSummary ? (
                    <div className="finding-impact-box">
                      <span className="summary-card-label">System impact</span>
                      <p className="finding-impact-copy">{systemImpactSummary}</p>
                    </div>
                  ) : null}
                  <a className="finding-evaluation-flow" href={coverageFlowHref(selectedSystem.id, coverageRow)}>
                    <span>Evaluation flow</span>
                    <strong className={`chip ${coverageFlowTone(coverageStatus)}`}>{coverageFlowLabel(coverageStatus)}</strong>
                    <em>{coverageMeta}</em>
                  </a>
                  <a className="pill-link" href={`#/systems/${selectedSystem.id}/findings/${finding.id}`}>
                    Open finding
                  </a>
                </div>

                <DisclosurePanel title="Details" className="finding-compact-details">
                  <div className="chip-row">
                    {finding.component ? (
                      <a className="chip chip-emphasis" href={`#/systems/${selectedSystem.id}/components/${finding.component.id}`}>
                        Component: {componentLabel(finding.component)}
                      </a>
                    ) : null}
                    <span className="chip">{findingOccurrenceLabel(finding)}</span>
                    {finding.prevalence ? <span className="chip">{prevalenceLabel(finding.prevalence)}</span> : null}
                    <span className="chip">Evidence {finding.evidence_count}</span>
                    <EvidencePostureChip posture={finding.evidence_posture} prefix="Freshness" />
                  </div>

                  <div className="finding-meta-grid">
                    <div className="finding-meta-item">
                      <span>Owner</span>
                      <strong>{ownerLabel(finding.owner)}</strong>
                    </div>
                    <div className="finding-meta-item">
                      <span>Confidence</span>
                      <strong>{formatPercent(finding.confidence)}</strong>
                    </div>
                  </div>

                  {targetedValidationSummary ? (
                    <div className="finding-validation-callout">
                      <div>
                        <p className="chapter-kicker">Validation</p>
                        <strong>{preparednessLabel(targetedValidationSummary.preparedness_status)}</strong>
                        <p>{targetedValidationSummary.coverage_note}</p>
                      </div>
                      <div className="finding-validation-actions">
                        <span className={`chip ${targetedValidationTone(targetedValidationSummary)}`}>
                          {preparednessLabel(targetedValidationSummary.preparedness_status)}
                        </span>
                        <a className="pill-link" href={targetedAction.href}>
                          {targetedAction.label}
                        </a>
                      </div>
                    </div>
                  ) : null}
                </DisclosurePanel>
              </article>
            );
          })}

          {!findings.length ? (
            <div className="empty-state-card">
              <strong>
                {activeFilterChips.length ? 'No current findings match this overview slice' : 'No significant issues detected yet'}
              </strong>
              <p>
                {activeFilterChips.length
                  ? 'Try clearing filters or returning to overview to choose a different severity, workflow, or component slice.'
                  : 'As workflows run, reliability findings will appear here with clearer prioritization, validation cues, and supporting context.'}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
